import axios from 'axios';
import moment from 'moment';

import { convertYearMonthDay } from './mapper';
import { publishApiToNewRelic } from './newRelic';
import {
  RESERVATIONS_AVAILABILITY_URL,
  RESERVATIONS_FDAV4_URL,
  RESERVATIONS_MODIFY_AVAILABILITY_URL,
  RESERVATION_CANCEL_URL,
  RESERVATION_LOOKUP_URL,
  RESERVATION_UPDATE_URL,
  MAX_VANTAGE_RETRIES
} from '../constants/constants';
import {
  getReservationMock,
  getFDAv4AvailabilityMock,
  getModifyAvailabilityMock,
  updateReservationMock
} from '../../mock-data/mockApiCalls';

// MAKE SURE THESE ARE ALWAYS SET TO FALSE BEFORE MERGE
const useMock = {
  fda: false,
  modifyFda: false,
  fdaV4: false,
  lookup: false,
  update: false,
  cancel: false
};

// There's an issue with Vantage sometimes failing for fun right now (Sept. 15, 2022)
// Once we have an actual fix (or a stronger bandaid), we should remove/repurpose this method.
function isVantageError(error) {
  return error.response && error.response.status === 500 && error.response.data === 'Error retrieving Vantage token';
}

async function retryCallOnVantageError(call, maxRetries = MAX_VANTAGE_RETRIES) {
  let retries = 0;
  while (true) {
    try {
      // eslint-disable-next-line no-await-in-loop
      return await call();
    } catch (error) {
      if (isVantageError(error) && retries < maxRetries) {
        retries += 1;
      } else throw error;
    }
  }
}

const cancelReservation = ({
  host,
  reservationId,
  uuid,
  cancelCode
}) => {
  let url = `${host}${RESERVATION_CANCEL_URL}`
    + `?reservationId=${reservationId}`
    + `&uuid=${uuid}`
    + `&cancelCode=${cancelCode}`;
  return axios
    .post(url)
    .then((res) => {
      publishApiToNewRelic(
        {
          action: 'success',
          output: {
            component: 'rental-reservation-form',
            apiName: 'Reservations (Production): Cancel',
            baseEndpoint: RESERVATION_CANCEL_URL,
            endpoint: url,
            reservationId,
            cancelCode,
            uuid,
            data: res.data
          }
        }
      );
      return res.data;
    })
    .catch((error) => {
      publishApiToNewRelic(
        {
          action: 'error',
          output: {
            component: 'rental-reservation-form',
            apiName: 'Reservations (Production): Cancel',
            baseEndpoint: RESERVATION_CANCEL_URL,
            endpoint: url,
            reservationId,
            cancelCode,
            uuid,
            error
          }
        }
      );
      throw error;
    });
};

async function getAvailability({
  host,
  stores,
  rentalCategory,
  rentalSubCategory,
  reservationDate,
  reservationTime,
  endDate,
  endTime
}) {
  let endpoint = `${host}${RESERVATIONS_AVAILABILITY_URL}`
  + `?categoryCode=${rentalCategory}&subCategoryCode=${rentalSubCategory}`
  + `&reservationDate=${moment(reservationDate, 'MM/DD/YYYY').format('YYYY-MM-DD')}`
  + `&endDate=${moment(endDate, 'MM/DD/YYYY').format('YYYY-MM-DD')}`;

  let storeList = stores.map((store) => store.storeId).join(',');
  endpoint += `&storeList=${storeList}`;

  const callAvailability = async () => {
    const response = useMock.fda
      ? await getFDAv4AvailabilityMock(2000, '01/01/2222')
      : await axios.get(endpoint);

    publishApiToNewRelic(
      {
        action: 'success',
        output: {
          component: 'rental-reservation-form',
          apiName: 'RENTAL-FORECAST-API',
          baseEndpoint: RESERVATIONS_AVAILABILITY_URL,
          endpoint,
          storeList,
          rentalCategory,
          rentalSubCategory,
          reservationDate,
          reservationTime,
          reservationDayOfWeek: moment(reservationDate, 'MM/DD/YYYY').format('dddd'),
          dropoffDate: endDate,
          dropoffTime: endTime,
          dropoffDayOfWeek: moment(endDate, 'MM/DD/YYYY').format('dddd'),
          data: response.data
        }
      }
    );
    return response.data;
  };

  try {
    return await retryCallOnVantageError(callAvailability);
  } catch (error) {
    console.error(`Failed when calling ${endpoint}`, error);
    publishApiToNewRelic(
      {
        action: 'error',
        output: {
          component: 'rental-reservation-form',
          apiName: 'RENTAL-FORECAST-API',
          baseEndpoint: RESERVATIONS_AVAILABILITY_URL,
          endpoint,
          storeList,
          rentalCategory,
          rentalSubCategory,
          reservationDate,
          reservationDayOfWeek: moment(reservationDate, 'MM/DD/YYYY').format('dddd'),
          dropoffDate: endDate,
          dropoffTime: endTime,
          dropoffDayOfWeek: moment(endDate, 'MM/DD/YYYY').format('dddd'),
          error
        }
      }
    );
    return [];
  }
}

async function getFDAv4Availability({
  host,
  storeList,
  rentalCategory,
  rentalSubCategory,
  startDate,
  startTime,
  endDate,
  endTime,
  reservationId
}) {
  let endpoint = `${host}${RESERVATIONS_FDAV4_URL}`;
  let body = {
    appFlow: 'reservation',
    storeList,
    tools: [
      {
        categoryCode: rentalCategory,
        subCategoryCode: rentalSubCategory,
        pickupTime: moment(startDate, 'MM/DD/YYYY').format('YYYY-MM-DD'),
        returnTime: moment(endDate, 'MM/DD/YYYY').format('YYYY-MM-DD'),
        count: 1,
      }
    ],
    reservationId,
  };

  const callFDAv4 = async () => {
    const response = useMock.fdaV4
      ? await getFDAv4AvailabilityMock(2000, '01/01/2222')
      : await axios.post(endpoint, body);

    publishApiToNewRelic(
      {
        action: 'success',
        output: {
          component: 'rental-reservation-form',
          apiName: 'RENTAL-INVENTORY-API-FDAV4',
          baseEndpoint: RESERVATIONS_FDAV4_URL,
          endpoint,
          storeList,
          rentalCategory,
          rentalSubCategory,
          reservationDate: startDate,
          reservationTime: startTime,
          reservationDayOfWeek: moment(startDate, 'MM/DD/YYYY').format('dddd'),
          dropoffDate: endDate,
          dropoffTime: endTime,
          dropoffDayOfWeek: moment(endDate, 'MM/DD/YYYY').format('dddd'),
          reservationId,
          data: response.data
        }
      }
    );
    return response.data;
  };

  try {
    return await retryCallOnVantageError(callFDAv4);
  } catch (error) {
    console.error(`Failed when calling ${endpoint}`, error);
    publishApiToNewRelic(
      {
        action: 'error',
        output: {
          component: 'rental-reservation-form',
          apiName: 'RENTAL-INVENTORY-API-FDAV4',
          baseEndpoint: RESERVATIONS_FDAV4_URL,
          endpoint,
          storeList,
          rentalCategory,
          rentalSubCategory,
          startDate,
          reservationDayOfWeek: moment(startDate, 'MM/DD/YYYY').format('dddd'),
          dropoffDate: endDate,
          dropoffTime: endTime,
          dropoffDayOfWeek: moment(endDate, 'MM/DD/YYYY').format('dddd'),
          reservationId,
          error
        }
      }
    );
    throw new Error('Unexpected Error');
  }
}

async function getModifyAvailability({
  host,
  storeId,
  reservationId,
  rentalCategory,
  rentalSubCategory,
  startDate,
  startTime,
  endDate,
  endTime
}) {

  let endpoint = `${host}${RESERVATIONS_MODIFY_AVAILABILITY_URL}`;
  let body = {
    rentalCategory,
    rentalSubCategory,
    storeId,
    reservationId,
    startDate: moment(startDate, 'MM/DD/YYYY').format('YYYY-MM-DD'),
    endDate: moment(endDate, 'MM/DD/YYYY').format('YYYY-MM-DD')
  };

  const callModifyAvailability = async () => {
    const response = useMock.modifyFda
      ? await getModifyAvailabilityMock(2000, '01/01/2222')
      : await axios.post(endpoint, body);

    publishApiToNewRelic(
      {
        action: 'success',
        output: {
          component: 'rental-reservation-form',
          apiName: 'RENTAL-MODIFY-FORECAST-API',
          baseEndpoint: RESERVATIONS_MODIFY_AVAILABILITY_URL,
          endpoint,
          storeList: storeId,
          rentalCategory,
          rentalSubCategory,
          reservationDate: startDate,
          reservationTime: startTime,
          reservationDayOfWeek: moment(startDate, 'MM/DD/YYYY').format('dddd'),
          dropoffDate: endDate,
          dropoffTime: endTime,
          dropoffDayOfWeek: moment(endDate, 'MM/DD/YYYY').format('dddd'),
          data: response.data
        }
      }
    );
    return response.data;
  };

  try {
    return await retryCallOnVantageError(callModifyAvailability);
  } catch (error) {
    console.error(`Failed when calling ${endpoint}`, error);
    publishApiToNewRelic(
      {
        action: 'error',
        output: {
          component: 'rental-reservation-form',
          apiName: 'RENTAL-MODIFY-FORECAST-API',
          baseEndpoint: RESERVATIONS_MODIFY_AVAILABILITY_URL,
          endpoint,
          storeList: storeId,
          rentalCategory,
          rentalSubCategory,
          startDate,
          reservationDayOfWeek: moment(startDate, 'MM/DD/YYYY').format('dddd'),
          dropoffDate: endDate,
          dropoffTime: endTime,
          dropoffDayOfWeek: moment(endDate, 'MM/DD/YYYY').format('dddd'),
          error
        }
      }
    );
    throw new Error('Unexpected Error');
  }
}

async function updateReservation({
  host,
  reservationId,
  start,
  end,
  uuid
}) {
  const endpoint = `${host}${RESERVATION_UPDATE_URL.replace('{reservationId}', reservationId)}`;

  const body = {
    user_id: 'Rent Online',
    pickup_time: convertYearMonthDay(start),
    return_time: convertYearMonthDay(end),
    uuid
  };

  const callUpdateReservation = async () => {
    const response = useMock.update
      ? await updateReservationMock(2000)
      : await axios.post(endpoint, body);

    if (response.status === 200) {
      publishApiToNewRelic(
        {
          action: 'success',
          output: {
            component: 'rental-reservation-form',
            apiName: 'RENTAL-RESERVATION-UPDATE',
            baseEndpoint: RESERVATION_UPDATE_URL,
            endpoint,
            reservationId,
            data: 'modification submitted successfully'
          }
        }
      );
      return { success: 'modification submitted successfully' };
    }
    return (response.data);
  };

  try {
    return await retryCallOnVantageError(callUpdateReservation);
  } catch (error) {
    console.error(`Failed when calling ${endpoint}`, error);
    publishApiToNewRelic(
      {
        action: 'error',
        output: {
          component: 'rental-reservation-form',
          apiName: 'RENTAL-RESERVATION-UPDATE',
          baseEndpoint: RESERVATION_UPDATE_URL,
          endpoint,
          reservationId,
          error
        }
      }
    );
    throw new Error('Unexpected Error');
  }
}

async function getReservation({ host, reservationId }) {
  const endpoint = `${host}${RESERVATION_LOOKUP_URL}/${reservationId}`;

  const callReservations = async () => {
    const response = useMock.lookup
      ? await getReservationMock(2000, reservationId)
      : await axios.get(endpoint);

    publishApiToNewRelic(
      {
        action: 'success',
        output: {
          component: 'rental-reservation-form',
          apiName: 'RENTAL-RESERVATION-LOOKUP',
          baseEndpoint: RESERVATION_LOOKUP_URL,
          endpoint,
          reservationId,
          data: response.data
        }
      }
    );
    return response.data;
  };

  try {
    return await retryCallOnVantageError(callReservations);
  } catch (error) {
    console.error(`Failed when calling ${endpoint}`, error);
    publishApiToNewRelic(
      {
        action: 'error',
        output: {
          component: 'rental-reservation-form',
          apiName: 'RENTAL-RESERVATION-LOOKUP',
          baseEndpoint: RESERVATION_LOOKUP_URL,
          endpoint,
          reservationId,
          error
        }
      }
    );
    throw new Error('Unexpected Error');
  }
}

export {
  cancelReservation,
  getAvailability,
  getModifyAvailability,
  getFDAv4Availability,
  getReservation,
  updateReservation
};
